import React, { FC } from 'react';
import { useLocation } from 'react-router-dom';
import {
  PrimaryNavigation,
  PrimaryNavigationExpansionItem,
  PrimaryNavigationItem,
  PrimaryNavigationSubItem,
} from '@lifeomic/chroma-react/components/PrimaryNavigation';
import sidebarNavItemsFl from './data/navItems_fl';
import {
  useActiveAccount,
  useActiveProjectId,
  usePolicy,
  useZoriKnowledgeBaseAccess,
} from '@lifeomic/phc-web-toolkit/src/hooks';
import { useIntl } from 'react-intl';
import urls from '../../../util/urls';
import messages from './data/messages';
import { memberDetailMessages } from '../../pages/Members/MemberDetail/messages';
import { useCurrentMemberNav } from '../../pages/Members/MemberDetail/CurrentMemberNavContext';
import type { MemberSection } from '../../pages/Members/MemberDetail/types';

import { makeStyles } from '@lifeomic/chroma-react/styles';
import { useSelector } from 'react-redux';
// @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
import { JupyterNotebooks } from '../icons/JupyterNotebooks';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { Home, User } from '@lifeomic/chromicons';
import getIsInFedEnvironment from '@lifeomic/phc-web-toolkit/src/util/getIsInFedEnvironment';
import EnforceAccountFeatureToggle from '@lifeomic/phc-web-toolkit/src/components/EnforceAccountFeatureToggle';
import useIsMobile from '@lifeomic/phc-web-toolkit/src/hooks/useIsMobile';
import { testIds as sidebarTestIds } from './index';
import { tiles, testIds as tilesTestIds } from '../../../common/tiles_fl';

const useStyles = makeStyles(theme => ({
  gapLine: {
    height: 1,
    background: theme.palette.black[100],
    margin: theme.spacing(2, -2),
    padding: 0,
  },
}));

const MEMBER_SECTION_LABEL_IDS: Record<
  Exclude<MemberSection, 'landing'>,
  keyof typeof memberDetailMessages
> = {
  'onboarding-snapshot': 'onboardingSnapshotLabel',
  pulse: 'pulseLabel',
  'regenerative-medicine-ops': 'regenerativeMedicineOpsLabel',
  'annual-report': 'annualReportLabel',
  'composite-report': 'compositeReportLabel',
  'action-plan': 'actionPlanLabel',
  'ai-tools': 'aiToolsLabel',
  'rule-action-audit': 'ruleActionAuditLabel',
  labs: 'labsLabel',
  documents: 'documentsLabel',
  surveys: 'surveysLabel',
  'omics-explorer': 'omicsExplorerLabel',
  'member-app': 'memberAppLabel',
};

export const FountainLifeSidebarNav: FC = () => {
  const { formatMessage } = useIntl();
  const classes = useStyles({});
  const { pathname } = useLocation();
  const activeAccount = useActiveAccount();
  const activeProjectId = useActiveProjectId();
  const policy = usePolicy();
  const features = useSelector(featuresSelectors.selectFeatures);
  const isMobile = useIsMobile();
  const { canViewTab: canViewZoriKnowledgeBase } = useZoriKnowledgeBaseAccess();

  const MEMBER_DETAIL_SECTIONS: Exclude<MemberSection, 'landing'>[] = [
    'onboarding-snapshot',
    ...(features?.zoriPulsePriorities ? (['pulse'] as const) : []),
    ...(features?.regenerativeMedicineOps
      ? (['regenerative-medicine-ops'] as const)
      : []),
    'annual-report',
    ...(features?.genericCompositeReport
      ? (['composite-report'] as const)
      : []),
    'action-plan',
    'ai-tools',
    ...(features?.fountainLifePowerUser
      ? (['rule-action-audit'] as const)
      : []),
    'labs',
    'documents',
    'surveys',
    'member-app',
  ];

  const memberDetailMatch = pathname.match(/^\/project\/members\/([^/]+)/);
  const currentMemberId = memberDetailMatch?.[1] ?? null;
  const isOnMemberDetailView = currentMemberId != null;
  const { memberDisplayName: currentMemberDisplayName } = useCurrentMemberNav();

  // Collapse sidebar navigation if user is on mobile
  React.useEffect(() => {
    const isSidebarCollapsed = JSON.parse(
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      window.localStorage.getItem('@@lifeomic/store/layoutSidebarCollapsed'),
    );

    if (isMobile && !isSidebarCollapsed) {
      window.localStorage.setItem(
        '@@lifeomic/store/layoutSidebarCollapsed',
        'true',
      );
    }
  }, [isMobile]);

  const filteredSidebarItems = sidebarNavItemsFl.filter(n =>
    n.item.shouldDisplay(activeAccount, policy, features),
  );

  return (
    <PrimaryNavigation>
      {activeProjectId && (
        <PrimaryNavigationItem
          to={urls.app.projectLandingPage(activeProjectId)}
          icon={<Home />}
          label={formatMessage(messages.home)}
          key={-1}
        />
      )}
      {filteredSidebarItems.map((n, i) => {
        const Icon = n.item.navIcon || n.item.tileIcon;
        const requiresActiveProject =
          n.requiresActiveProject || n.item.requiresProject;

        if (n.item.id === 'omics') {
          if (requiresActiveProject && !activeProjectId) {
            return null;
          }

          return (
            <PrimaryNavigationExpansionItem
              key={i}
              icon={Icon ? <Icon /> : null}
              to={n.item.uri}
              label={n.item.title}
              rootParentPath={urls.app.omics.root()}
              {...getTestProps(sidebarTestIds.omicsRoot)}
            >
              {tiles.genomicsIngestions.shouldDisplay(
                activeAccount,
                policy,
              ) && (
                <PrimaryNavigationSubItem
                  to={tiles.genomicsIngestions.uri}
                  label={tiles.genomicsIngestions.title}
                  {...getTestProps(tilesTestIds.genomicsIngestions)}
                />
              )}
              <PrimaryNavigationSubItem
                to={tiles.genomicsTests.uri}
                label={tiles.genomicsTests.title}
                {...getTestProps(tilesTestIds.genomicsTests)}
              />
              {tiles.genomicsActions.shouldDisplay(activeAccount, policy) && (
                <PrimaryNavigationSubItem
                  to={tiles.genomicsActions.uri}
                  label={tiles.genomicsActions.title}
                  {...getTestProps(tilesTestIds.genomicsActions)}
                />
              )}
            </PrimaryNavigationExpansionItem>
          );
        }

        if (n.item.id === 'aiRules') {
          if (requiresActiveProject && !activeProjectId) {
            return null;
          }

          return (
            <PrimaryNavigationExpansionItem
              key={i}
              icon={Icon ? <Icon /> : null}
              to={n.item.uri}
              label={n.item.title}
              rootParentPath={n.item.uri}
              {...getTestProps(n.item.testId)}
            >
              <PrimaryNavigationSubItem
                to={urls.app.workflows.zoriRules('rules')}
                label="Rules"
              />
              {canViewZoriKnowledgeBase && (
                <PrimaryNavigationSubItem
                  to={urls.app.workflows.zoriRules('knowledge-base')}
                  label="Knowledge Base"
                />
              )}
              {features?.fountainLifePowerUser && (
                <PrimaryNavigationSubItem
                  to={urls.app.workflows.zoriRules('tasks')}
                  label="Tasks"
                />
              )}
              {features?.fountainLifePowerUser && (
                <PrimaryNavigationSubItem
                  to={urls.app.workflows.zoriRules('rule-actions')}
                  label="Rule Actions"
                />
              )}
              {features?.fountainLifePowerUser && (
                <PrimaryNavigationSubItem
                  to={urls.app.workflows.zoriStats()}
                  label="Stats"
                />
              )}
              {canViewZoriKnowledgeBase && (
                <PrimaryNavigationSubItem
                  to={urls.app.workflows.zoriClinicalReports()}
                  label="Clinical Reports"
                />
              )}
              {tiles.reportTemplates.shouldDisplay(activeAccount) && (
                <PrimaryNavigationSubItem
                  to={tiles.reportTemplates.uri}
                  label={tiles.reportTemplates.title}
                  {...getTestProps(tilesTestIds.reportTemplates)}
                />
              )}
              {tiles.zoriChat.shouldDisplay(
                activeAccount,
                policy,
                features,
              ) && (
                <PrimaryNavigationSubItem
                  to={tiles.zoriChat.uri}
                  label={tiles.zoriChat.title}
                  {...getTestProps(tilesTestIds.zoriChat)}
                />
              )}
            </PrimaryNavigationExpansionItem>
          );
        }

        if (
          n.item.id === 'subjectsSearch' &&
          isOnMemberDetailView &&
          currentMemberId
        ) {
          if (requiresActiveProject && !activeProjectId) {
            return null;
          }
          const memberRootPath = urls.app.members(currentMemberId);
          const memberNameLabel =
            currentMemberDisplayName != null
              ? currentMemberDisplayName
              : 'Member';
          return (
            <React.Fragment key={i}>
              <PrimaryNavigationItem
                to={n.item.uri}
                icon={Icon ? <Icon /> : null}
                label={n.item.title}
                {...getTestProps(n.item.testId)}
              />
              <PrimaryNavigationExpansionItem
                icon={<User />}
                to={memberRootPath}
                label={memberNameLabel}
                rootParentPath={memberRootPath}
                {...getTestProps(n.item.testId)}
              >
                {MEMBER_DETAIL_SECTIONS.map(section => (
                  <PrimaryNavigationSubItem
                    key={section}
                    to={`${memberRootPath}/${section}`}
                    label={formatMessage(
                      memberDetailMessages[MEMBER_SECTION_LABEL_IDS[section]],
                    )}
                  />
                ))}
                <EnforceAccountFeatureToggle accountFeature="genomics">
                  <PrimaryNavigationSubItem
                    to={`${memberRootPath}/omics-explorer`}
                    label={formatMessage(
                      memberDetailMessages.omicsExplorerLabel,
                    )}
                  />
                </EnforceAccountFeatureToggle>
              </PrimaryNavigationExpansionItem>
            </React.Fragment>
          );
        }

        return requiresActiveProject && !activeProjectId ? null : (
          <PrimaryNavigationItem
            key={i}
            to={n.item.uri}
            icon={Icon ? <Icon /> : null}
            label={n.item.title}
            {...getTestProps(n.item.testId)}
          />
        );
      })}
      {filteredSidebarItems.length > 0 && <li className={classes.gapLine}></li>}
      <EnforceAccountFeatureToggle accountFeature="notebooks">
        {activeAccount &&
          !getIsInFedEnvironment() &&
          activeAccount.type === 'ENTERPRISE' &&
          activeAccount.products?.includes('Notebooks') && (
            <PrimaryNavigationItem
              to={urls.jupyterNotebooks()}
              icon={<JupyterNotebooks />}
              label={formatMessage(messages.notebooksLabel)}
              {...getTestProps(sidebarTestIds.notebooks)}
            />
          )}
      </EnforceAccountFeatureToggle>
    </PrimaryNavigation>
  );
};

FountainLifeSidebarNav.displayName = 'FountainLifeSidebarNav';
export default FountainLifeSidebarNav;
